// Database Types (copied from shared package)

export type OfferHoldKind = 'unreleased' | 'release' | 'meta' | 'build' | 'spike' | 'ramp';

export interface OfferHold {
  kind: OfferHoldKind;
  /** ISO time the offer comes back (ramp: reaches 100%); null — not in the store yet. */
  until: string | null;
  /** Ramp only: share of devices that already get the offer, 0..1. */
  share?: number;
}

export interface App {
  id: string;
  name: string;
  platform: 'ios' | 'android';
  bundle_id: string;
  api_key: string;
  status: 'active' | 'paused' | 'deleted';
  store_version_ios: string | null;
  store_version_android: string | null;
  store_versions_checked_at: string | null;
  store_url: string | null;
  force_offer_url: string | null;
  force_offer_enabled: number;
  force_offer_off_reason: string | null;
  /** Force offer on hold after a release / update / install spike (worker lib/offer-hold.ts). Computed by the list endpoint. */
  offer_hold?: OfferHold | null;
  skip_version_check: number;
  antimoderation_off: number;
  /** ISO-2 через запятую: антимодерка выключена только в этих IP-гео. */
  antimoderation_off_countries: string | null;
  /** ISO-2 через запятую: гео, куда нельзя лить (бренд там недоступен). Только пометка. */
  forbidden_countries?: string | null;
  /** 1 = hosted in an outside store account (publisher's / partner's), not ours. */
  external_account: number;
  publisher_name?: string | null;
  /** CI build source (Apps Setup → Build); null when the app was never configured for the factory. */
  build_repo_id?: number | null;
  build_repo_name?: string | null;
  icon_url: string | null;
  description: string | null;
  release_notes_keywords: string | null;
  vertical: 'slots' | 'crash' | 'betting' | null;
  short_description: string | null;
  pause_reason: string | null;
  is_banned: number;
  in_work: number;
  banned_at: string | null;
  ban_reason: string | null;
  advertised_at: string | null;
  created_at: string;
  review_state: string | null;
  review_state_since: string | null;
  owner_admin_id: string | null;
  created_by_admin_id: string | null;
  owner_name: string | null;
  owner_email: string | null;
  /** Play console that holds the package (play_apps → play_accounts); null = not in any of ours. */
  play_account_id?: number | null;
  play_account_name?: string | null;
  /** Countries the app is open in on Play (production countryAvailability), JSON array of ISO-2. */
  play_countries_json?: string | null;
  play_countries_rest_of_world?: number | null;
  play_countries_checked_at?: number | null;
  play_countries_error?: string | null;
  /** Linked Firebase project id (or the legacy account name); null = no Firebase. */
  firebase_project?: string | null;
  /** 1 when a google-services.json can be downloaded for the app. */
  has_google_services_json?: number;
  /** A new Firebase project started from this row and not finished yet. */
  firebase_provision_id?: number | null;
  firebase_provision_state?: string | null;
  firebase_provision_error?: string | null;
  // A browser-profile run left a screenshot of where it stopped.
  firebase_provision_has_evidence?: number | boolean | null;
  comments_count?: number;
  last_comment?: string | null;
}

export interface Campaign {
  id: string;
  name: string;
  status: 'active' | 'paused' | 'completed';
  archived_at: string | null;
  start_date: string | null;
  end_date: string | null;
  created_at: string;
}

export interface Ad {
  id: string;
  campaign_id: string;
  ad_type: 'interstitial' | 'rewarded' | 'banner' | 'native' | 'popup';
  media_type: 'image' | 'video' | 'html' | 'native';
  media_url: string;
  click_url: string | null;
  duration_sec: number | null;
  lock_webview: boolean;
  icon_url: string | null;
  ad_text: string | null;
  ad_body: string | null;
  cta_text: string | null;
  cta_text_2: string | null;
  popup_delay_sec: number | null;
  popup_repeat_sec: number | null;
  store_url: string | null;
  status: 'active' | 'paused';
  archived_at: string | null;
  created_at: string;
  updated_at: string | null;
}

export interface AdTargeting {
  id: string;
  ad_id: string;
  countries: string | null;
  platforms: string | null;
  device_types: string | null;
  languages: string | null;
}

export interface AppCampaign {
  id: string;
  name: string;
  status: 'active' | 'paused' | 'completed';
  start_date: string | null;
  end_date: string | null;
  created_at: string;
  assignment_id: string;
  assigned_at: string;
}

export interface AdStats {
  impressions: number;
  clicks: number;
  ctr: number;
  installs: number;
  registrations: number;
  deposits: number;
  /** Install → Registration conversion rate, % */
  i2r: number;
  /** Registration → Deposit conversion rate, % */
  r2d: number;
  /** Install → Deposit conversion rate, % */
  i2d: number;
}

export interface AdWithTargeting extends Ad {
  targeting?: AdTargeting | null;
  stats?: AdStats;
}

export interface StatsHourly {
  id: string;
  ad_id: string;
  app_id: string;
  hour: string;
  country: string;
  platform: string;
  impressions: number;
  clicks: number;
  completes: number;
  requests: number;
}

export interface StatsDaily {
  id: string;
  ad_id: string;
  app_id: string;
  date: string;
  country: string;
  platform: string;
  impressions: number;
  clicks: number;
  completes: number;
  requests: number;
}

// Offer Types

export type OfferCurrency = 'USD' | 'EUR';

export interface Offer {
  id: string;
  partner: string;
  geo: string;
  brand: string | null;
  payout_cents: number | null;
  currency: OfferCurrency;
  lang: string | null;
  kpi: string | null;
  contact: string | null;
  offer_url: string | null;
  cap: number | null;
  cap_deps: number;
  status: 'active' | 'paused';
  wager: string | null;
  kpi_text: string | null;
  mindep_bl: string | null;
  rs: string | null;
  traffic_source: string | null;
  vertical: 'slots' | 'crash' | 'betting' | null;
  deal_model: 'cpa' | 'spend';
  budget: string | null;
  pay_type: 'prepay' | 'postpay' | null;
  report_url: string | null;
  cap_group_id: string | null;
  created_by_admin_id: string | null;
  created_by_name: string | null;
  created_by_email: string | null;
  created_at: string;
  clicks: number;
  regs: number;
  deps: number;
}

export interface CreateOfferInput {
  partner: string;
  geo: string;
  brand?: string | null;
  payout_cents?: number | null;
  currency?: OfferCurrency;
  lang?: string | null;
  kpi?: string | null;
  contact?: string | null;
  offer_url?: string | null;
  cap?: number | null;
  wager?: string | null;
  kpi_text?: string | null;
  mindep_bl?: string | null;
  rs?: string | null;
  traffic_source?: string | null;
  vertical?: string | null;
  deal_model?: 'cpa' | 'spend';
  budget?: string | null;
  pay_type?: 'prepay' | 'postpay' | null;
  report_url?: string | null;
  status?: 'active' | 'paused';
}

export interface UpdateOfferInput {
  partner?: string;
  geo?: string;
  brand?: string | null;
  payout_cents?: number | null;
  currency?: OfferCurrency;
  lang?: string | null;
  kpi?: string | null;
  contact?: string | null;
  offer_url?: string | null;
  cap?: number | null;
  status?: 'active' | 'paused';
  wager?: string | null;
  kpi_text?: string | null;
  mindep_bl?: string | null;
  rs?: string | null;
  traffic_source?: string | null;
  vertical?: string | null;
  deal_model?: 'cpa' | 'spend';
  budget?: string | null;
  pay_type?: 'prepay' | 'postpay' | null;
  report_url?: string | null;
  /** Super-admin only: reassign or clear the person responsible for the offer. */
  created_by_admin_id?: string | null;
  /** Full list of OTHER offer ids to share the cap with. `[]` detaches. */
  shared_offer_ids?: string[];
  reset_cap_deps?: boolean;
}

// API Response Types

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

// Auth Types

export type AdminSection =
  | 'dashboard'
  | 'apps'
  | 'apps-setup'
  | 'campaigns'
  | 'ads'
  | 'pwa'
  | 'funnel'
  | 'generate'
  | 'referrals'
  | 'devices'
  | 'offers'
  | 'offers-performance'
  | 'funnel-audits'
  // Monthly reconciliation with advertisers. Granted per person: it is not part
  // of any role preset and is never narrowed by campaign scope.
  | 'finance'
  // The outside REST API: own bearer keys, the PWAs provisioned with them, the
  // docs. Granted through the `api` addon only.
  | 'public-api'
  // The live browser: the Browser Profiles page and opening a profile on the
  // VPS. Granted through the `browser` addon only; managing profiles (their
  // logins, proxies, workspaces) stays with super admins.
  | 'browser'
  // App x geo daily performance report (tracker funnel vs Firebase arrivals).
  | 'perf'
  // iGaming Radar: casino brands, affiliate programs, demand by country.
  | 'radar'
  // ASO.Market: buying ratings, reviews and installs. Granted per person.
  | 'aso-market'
  | 'notifications'
  | 'settings'
  | 'tracker'
  | 'domains'
  | 'firebase'
  | 'play'
  | 'users'
  | 'audit'
  | 'checklist'
  | 'credentials'
  | 'facebook'
  | 'push'
  | 'push-test'
  | 'email'
  | 'qa'
  // Three independent wiki grants — see the note in api/shared/src/types.
  | 'wiki'
  | 'wiki-edit'
  | 'wiki-share';

export const ADMIN_SECTIONS: AdminSection[] = [
  'dashboard',
  'apps',
  'apps-setup',
  'campaigns',
  'ads',
  'pwa',
  'funnel',
  'generate',
  'referrals',
  'devices',
  'offers',
  'offers-performance',
  'funnel-audits',
  'finance',
  'public-api',
  'browser',
  'perf',
  'radar',
  'aso-market',
  'notifications',
  'settings',
  'tracker',
  'domains',
  'firebase',
  'play',
  'users',
  'audit',
  'checklist',
  'credentials',
  'facebook',
  'push',
  'push-test',
  'email',
  'qa',
  'wiki',
  'wiki-edit',
  'wiki-share',
];

export const DEVELOPER_SECTIONS: AdminSection[] = [
  'apps',
  'apps-setup',
  'firebase',
  'play',
  'push-test',
  'wiki',
];

export const TRAFFIC_SECTIONS: AdminSection[] = [
  'apps',
  'pwa',
  'tracker',
  'notifications',
  'wiki',
];

export const BDM_SECTIONS: AdminSection[] = [
  'apps',
  'tracker',
  'domains',
  'offers',
  'offers-performance',
  'perf',
  'funnel-audits',
  'firebase',
  'credentials',
  'wiki',
];

export const QA_SECTIONS: AdminSection[] = [
  'dashboard',
  'apps',
  'campaigns',
  'ads',
  'pwa',
  'funnel',
  'generate',
  'devices',
  'offers',
  'notifications',
  'tracker',
  'audit',
  'checklist',
  'push',
  'email',
  'qa',
  'wiki',
];

// One base role decides app/campaign scoping; addon roles only add sections and
// capabilities on top, so a person can be e.g. QA and developer at once.
export type AdminBaseRole = 'general' | 'aso' | 'traffic' | 'bdm';
export type AdminAddonRole = 'developer' | 'qa' | 'finance' | 'api' | 'browser';
export const ADMIN_ADDON_ROLES: AdminAddonRole[] = ['developer', 'qa', 'finance', 'api', 'browser'];

// Telegram cards a person may decide. A super admin holds them all.
export type ApprovalKind =
  | 'delete' | 'credential_password' | 'credential_totp' | 'facebook_secret' | 'pr_merge' | 'publisher_payment'
  | 'keystore_change' | 'asomarket_order';
export const APPROVAL_KINDS: ApprovalKind[] = [
  'delete', 'credential_password', 'credential_totp', 'facebook_secret', 'pr_merge', 'publisher_payment',
  'keystore_change', 'asomarket_order',
];

export interface Admin {
  id: string;
  email: string;
  name?: string | null;
  role: 'super_admin' | 'user';
  user_type: 'general' | 'aso' | 'traffic' | 'bdm';
  addons: AdminAddonRole[];
  /** Effective: every kind for a super admin. */
  approvals?: ApprovalKind[];
  telegram_linked?: boolean;
  permissions: AdminSection[];
  app_access_mode: 'legacy_all' | 'assigned';
  allowed_apps: string[];
  allowed_tracker_campaigns: string[];
  manual_tracker_campaigns: string[];
  allowed_tracker_pwas: string[];
  assistant_of_admin_id: string | null;
  assistant_of_name: string | null;
  assistant_of_email: string | null;
  read_only: boolean;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface AuthResponse {
  token: string;
  admin: Admin;
  message?: string;
}

// Input Types for Create/Update

export interface CreateAppInput {
  name: string;
  platform: 'ios' | 'android';
  bundle_id: string;
  store_url?: string | null;
  icon_url?: string | null;
  description?: string | null;
  release_notes_keywords?: string | null;
  vertical?: string | null;
  owner_admin_id?: string;
}

export interface UpdateAppInput {
  name?: string;
  platform?: 'ios' | 'android';
  bundle_id?: string;
  status?: 'active' | 'paused';
  pause_reason?: string | null;
  store_url?: string | null;
  force_offer_url?: string | null;
  force_offer_enabled?: boolean;
  force_offer_off_reason?: string | null;
  skip_version_check?: boolean;
  antimoderation_off?: boolean;
  antimoderation_off_countries?: string[];
  forbidden_countries?: string[];
  external_account?: boolean;
  icon_url?: string | null;
  description?: string | null;
  release_notes_keywords?: string | null;
  is_banned?: boolean;
  in_work?: boolean;
  vertical?: string | null;
}

export interface CreateCampaignInput {
  name: string;
  app_id: string;
  status?: 'active' | 'paused' | 'completed';
  start_date?: string | null;
  end_date?: string | null;
}

export interface UpdateCampaignInput {
  name?: string;
  status?: 'active' | 'paused' | 'completed';
  start_date?: string | null;
  end_date?: string | null;
}

export interface CreateAdInput {
  campaign_id: string;
  ad_type: 'interstitial' | 'rewarded' | 'banner' | 'native' | 'popup';
  media_type: 'image' | 'video' | 'html' | 'native';
  media_url: string;
  click_url?: string | null;
  duration_sec?: number | null;
  lock_webview?: boolean;
  icon_url?: string | null;
  ad_text?: string | null;
  ad_body?: string | null;
  cta_text?: string | null;
  cta_text_2?: string | null;
  popup_delay_sec?: number | null;
  popup_repeat_sec?: number | null;
  store_url?: string | null;
  status?: 'active' | 'paused';
  targeting?: {
    countries?: string | null;
    platforms?: string | null;
    device_types?: string | null;
    languages?: string | null;
  };
}

export interface UpdateAdInput {
  campaign_id?: string;
  ad_type?: 'interstitial' | 'rewarded' | 'banner' | 'native' | 'popup';
  media_type?: 'image' | 'video' | 'html' | 'native';
  media_url?: string;
  click_url?: string | null;
  duration_sec?: number | null;
  lock_webview?: boolean;
  icon_url?: string | null;
  ad_text?: string | null;
  ad_body?: string | null;
  cta_text?: string | null;
  cta_text_2?: string | null;
  popup_delay_sec?: number | null;
  popup_repeat_sec?: number | null;
  store_url?: string | null;
  status?: 'active' | 'paused';
  targeting?: {
    countries?: string | null;
    platforms?: string | null;
    device_types?: string | null;
    languages?: string | null;
  } | null;
}

export interface UploadUrlResponse {
  key: string;
  content_type: string;
}

export interface UploadResponse {
  url: string;
  key: string;
}

// PWA Types

export interface PwaApp {
  id: string;
  app_name: string;
  developer_name: string;
  category: string;
  rating: number;
  reviews_count: number;
  downloads_count: string;
  size: string;
  version: string;
  short_description: string;
  full_description: string;
  icon_url: string;
  splash_url: string;
  bg_color: string;
  screenshots: string;
  videos: string;
  offer_url: string;
  proxy_url: string | null;
  ab_enabled: number;
  ab_group: string | null;
  skip_landing_pct: number;
  descriptions_i18n: string;
  store_layout: 'auto' | 'play' | 'app';
  status: 'active' | 'paused' | 'deleted';
  created_at: string;
  updated_at: string;
  total_page_visits?: number;
  total_install_clicks?: number;
  total_install_completes?: number;
  total_pwa_opens?: number;
  fb_pixel_id?: string | null;
  fb_access_token?: string | null;
  fb_test_event_code?: string | null;
  fb_events_json?: string;
  reviews_json: string;
  push_enabled?: number;
  push_firebase_account_id?: number | null;
  push_firebase_project_id?: string | null;
}

export interface PwaReviewItem {
  id: string;
  name: string;
  photoUrl?: string;
  rating: number;
  date: string;
  text: string;
  reply?: string;
  featured?: boolean;
}

export interface PwaReviewI18nEntry {
  text: string;
  reply?: string;
}

export interface PwaReviewsConfig {
  items: PwaReviewItem[];
  i18n: Record<string, PwaReviewI18nEntry[]>;
}

export interface PwaStatsDaily {
  date: string;
  page_visits: number;
  install_clicks: number;
  install_completes: number;
  pwa_opens: number;
}

export interface PwaEngagementStats {
  sessions: number;
  reviews_views: number;
  avg_dwell_before_click_ms: number | null;
  avg_scroll_pct: number | null;
  avg_time_on_page_ms: number | null;
  avg_active_time_ms: number | null;
  reached_reviews_count: number;
  reached_bottom_count: number;
  read_more_count: number;
  screenshot_interacted_count: number;
  bounces: number;
}

export interface PwaScrollDistribution {
  b0_25: number;
  b25_50: number;
  b50_75: number;
  b75_100: number;
  b100: number;
}

export interface PwaDeviceBreakdown {
  device_type: string;
  count: number;
}

export interface PwaStatsResponse {
  daily: PwaStatsDaily[];
  totals: {
    page_visits: number;
    install_clicks: number;
    install_completes: number;
    pwa_opens: number;
  };
  engagement: PwaEngagementStats;
  scroll_distribution: PwaScrollDistribution;
  devices: PwaDeviceBreakdown[];
}

export interface CreatePwaInput {
  app_name: string;
  developer_name?: string;
  category?: string;
  rating?: number;
  reviews_count?: number;
  downloads_count?: string;
  size?: string;
  version?: string;
  short_description?: string;
  full_description?: string;
  icon_url: string;
  splash_url?: string;
  bg_color?: string;
  screenshots?: string;
  videos?: string;
  offer_url: string;
  proxy_url?: string | null;
  ab_enabled?: boolean;
  ab_group?: string | null;
  skip_landing_pct?: number;
  descriptions_i18n?: string;
  status?: 'active' | 'paused';
  fb_pixel_id?: string | null;
  fb_access_token?: string | null;
  fb_test_event_code?: string | null;
  fb_events_json?: string;
  reviews_json?: string;
}

export interface UpdatePwaInput {
  app_name?: string;
  developer_name?: string;
  category?: string;
  rating?: number;
  reviews_count?: number;
  downloads_count?: string;
  size?: string;
  version?: string;
  short_description?: string;
  full_description?: string;
  icon_url?: string;
  splash_url?: string;
  bg_color?: string;
  screenshots?: string;
  videos?: string;
  offer_url?: string;
  proxy_url?: string | null;
  ab_enabled?: boolean;
  ab_group?: string | null;
  skip_landing_pct?: number;
  descriptions_i18n?: string;
  status?: 'active' | 'paused';
  fb_pixel_id?: string | null;
  fb_access_token?: string | null;
  fb_test_event_code?: string | null;
  fb_events_json?: string;
  reviews_json?: string;
  push_enabled?: number;
  push_firebase_account_id?: number | null;
  push_firebase_project_id?: string | null;
}

export interface PwaVariant {
  id: string;
  pwa_id: string;
  app_name: string;
  developer_name: string;
  icon_url: string;
  weight: number;
  enabled: number;
  created_at: number;
  updated_at: number;
}

export interface PwaVariantStats extends PwaVariant {
  impressions: number;
  installs: number;
  cr: number;
}

export interface CreateVariantInput {
  app_name: string;
  developer_name: string;
  icon_url: string;
  weight?: number;
  enabled?: boolean;
}

export type UpdateVariantInput = Partial<CreateVariantInput>;

export interface PartnerCredential {
  id: string;
  name: string;
  login: string | null;
  url: string | null;
  tg_chat: string | null;
  comment: string | null;
  created_at: string;
  updated_at: string;
  has_password: boolean;
  has_email: boolean;
  has_totp: boolean;
}

export interface CredentialSecret {
  password: string | null;
  email: string | null;
  /** Second at which the approval window closes; null for a super admin. */
  grant_expires_at?: number | null;
}

/** The current 2FA code of a credential; the key itself never leaves the server. */
export interface CredentialTotpCode {
  code: string;
  seconds_left: number;
  period: number;
  /** Second at which the approval window closes; null for a super admin. */
  grant_expires_at: number | null;
}

/** What a reveal answers with when it needs a super admin's approval first. */
export interface CredentialRevealPending {
  pending_approval: true;
  kind: 'reveal';
  request_id: string;
  expires_at: number;
}

export interface CreateCredentialInput {
  name: string;
  login?: string | null;
  password?: string | null;
  email?: string | null;
  url?: string | null;
  tg_chat?: string | null;
  comment?: string | null;
  /** Base32 key or otpauth:// link; empty clears it on update. */
  totp_secret?: string | null;
}

export type UpdateCredentialInput = Partial<CreateCredentialInput>;

export type FbFieldType = 'text' | 'textarea' | 'url' | 'number';

export interface FbFieldDef {
  id: string;
  key: string;
  label: string;
  type: FbFieldType;
  is_secret: boolean;
  position: number;
}

export interface FbAccountListItem {
  id: string;
  name: string;
  values: Record<string, string>;   // non-secret only
  has: Record<string, boolean>;     // secret key -> populated?
  created_at: string;
  updated_at: string;
}

export interface FbAccountSecret {
  values: Record<string, string | null>;
  /** Second at which the approval window closes; null for a super admin. */
  grant_expires_at?: number | null;
}

export interface CreateFbFieldInput {
  label: string;
  type: FbFieldType;
  is_secret: boolean;
  position?: number;
}
export type UpdateFbFieldInput = Partial<CreateFbFieldInput>;

export interface CreateFbAccountInput {
  name: string;
  values: Record<string, string>;
}
export type UpdateFbAccountInput = Partial<CreateFbAccountInput>;
